import React, { useState, useEffect } from 'react';
import {
  UserIcon,
  ShieldCheckIcon,
  BuildingOffice2Icon,
  KeyIcon,
  SwatchIcon,
  BellAlertIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import ProfileTab from './ProfileTab';
import SecurityTab from './SecurityTab';
import OrganizationTab from './OrganizationTab';
import ApiKeysTab from './ApiKeysTab';
import AppearanceTab from './AppearanceTab';
import NotificationsTab from './NotificationsTab';
import DangerZoneTab from './DangerZoneTab';

const TABS = [
  { id: 'profile', label: 'Profile', icon: UserIcon },
  { id: 'appearance', label: 'Appearance', icon: SwatchIcon },
  { id: 'notifications', label: 'Notifications', icon: BellAlertIcon },
  { id: 'security', label: 'Security', icon: ShieldCheckIcon },
  { id: 'organization', label: 'Organization', icon: BuildingOffice2Icon },
  { id: 'api-keys', label: 'API Keys', icon: KeyIcon },
  { id: 'danger', label: 'Danger Zone', icon: ExclamationTriangleIcon },
];

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileTab onNotify={showNotification} />;
      case 'appearance':
        return <AppearanceTab onNotify={showNotification} />;
      case 'notifications':
        return <NotificationsTab onNotify={showNotification} />;
      case 'security':
        return <SecurityTab onNotify={showNotification} />;
      case 'organization':
        return <OrganizationTab onNotify={showNotification} />;
      case 'api-keys':
        return <ApiKeysTab onNotify={showNotification} />;
      case 'danger':
        return <DangerZoneTab onNotify={showNotification} />;
      default:
        return <ProfileTab onNotify={showNotification} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-white">Settings</h1>
            <p className="text-gray-400 mt-2">Manage your account, security, organization, and API keys</p>
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div
          role={notification.type === 'error' ? 'alert' : 'status'}
          aria-live={notification.type === 'error' ? 'assertive' : 'polite'}
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
            notification.type === 'error' ? 'bg-red-600' : 'bg-green-600'
          } text-white`}
        >
          <div className="flex items-center gap-2">
            {notification.type === 'error' ? (
              <ExclamationTriangleIcon className="h-5 w-5" aria-hidden="true" />
            ) : (
              <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
            )}
            {notification.message}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Tab Sidebar */}
          <div className="md:w-56 flex-shrink-0">
            <nav
              className="space-y-1"
              role="tablist"
              aria-orientation="vertical"
              aria-label="Settings sections"
            >
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    id={`settings-tab-${tab.id}`}
                    aria-selected={isActive}
                    aria-controls="settings-tabpanel"
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-colors motion-reduce:transition-none ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <Icon className={`h-5 w-5 mr-3 ${isActive ? 'text-white' : 'text-gray-500'}`} aria-hidden="true" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div
            className="flex-1 min-w-0"
            role="tabpanel"
            id="settings-tabpanel"
            aria-labelledby={`settings-tab-${activeTab}`}
            tabIndex={0}
          >
            {renderTab()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
